import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import { parseCsv } from '../lib/parseCsv.js';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';

const PAGE_SIZE = 100;

// Matches config-backend's own per-call cap (routes/inboundSeats.js, worker.js: 50,000 rows) with
// a safety margin -- 'sourcesOnly' feeds (Pumpkin Ads' real inventory lists run up to ~244k rows
// per file) are split into sequential calls at this size. 'full' mode is NEVER chunked -- see the
// note on runPreview/confirmImport below for why that would silently corrupt the >50%-removal
// guard and floor reconciliation, which both require seeing one entire snapshot in a single call.
const SOURCES_ONLY_CHUNK_SIZE = 40000;

/**
 * Workstream 3: bulk supply onboarding for one inbound seat. Two import modes, matching the two
 * real feed shapes this platform has actually seen (see config-backend's
 * src/importers/bundleSupplyImport.js for the full server-side design):
 *
 * - "full" (Cuedart's real feed shape): every row is a bundle/domain + media type + floor CPM.
 *   Bulk-upserts BOTH inbound_seat_sources (routing) and inbound_seat_bundle_floors (pricing) in
 *   ONE server call -- never chunked client-side, because the >50%-removed safety guard and the
 *   full insert/update/remove floor reconciliation both need to see the entire new snapshot at
 *   once; splitting it across calls would make each call think every row outside itself should be
 *   removed.
 * - "sourcesOnly" (Pumpkin Ads' real feed shape: CTV / in-app video / web banner / web video
 *   inventory lists, each just a bare list of bundle ids or domains, no floor data at all):
 *   bulk-upserts ONLY inbound_seat_sources. Since sources are insert/update-only in EITHER mode
 *   (see the shared module's header), there is no cross-call snapshot to preserve, so a large file
 *   is safely split into sequential SOURCES_ONLY_CHUNK_SIZE-row calls, each independently
 *   committed. A currency field/removal guard/"force" checkbox make no sense here and are hidden.
 */
export default function SeatBundleFloorsPage() {
  const { seatId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [seat, setSeat] = useState(null);
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [floors, setFloors] = useState({ total: 0, rows: [] });
  const [floorsLoading, setFloorsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);

  const [batches, setBatches] = useState([]);

  const [mode, setMode] = useState('full');
  const [publisherId, setPublisherId] = useState('');
  const [identityType, setIdentityType] = useState('bundle');
  const [currency, setCurrency] = useState('USD');
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState(null);
  const [parseError, setParseError] = useState(null);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [force, setForce] = useState(false);
  const [importing, setImporting] = useState(false);
  const [chunkProgress, setChunkProgress] = useState(null); // { current, total } while chunked

  async function loadSeatAndPublishers() {
    setLoading(true);
    setError(null);
    try {
      const [detail, pubs] = await Promise.all([api.getInboundSeat(seatId), api.listPublishers().catch(() => [])]);
      setSeat(detail);
      setPublishers(pubs);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load seat.');
    } finally {
      setLoading(false);
    }
  }

  async function loadFloors() {
    setFloorsLoading(true);
    try {
      const result = await api.listSeatBundleFloors(seatId, { limit: PAGE_SIZE, offset: page * PAGE_SIZE, search });
      setFloors(result);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not load bundle floors.');
    } finally {
      setFloorsLoading(false);
    }
  }

  async function loadBatches() {
    try {
      setBatches(await api.listSeatBundleImportBatches(seatId));
    } catch {
      // Import history is a nice-to-have on this page -- a failed fetch here shouldn't block the
      // rest of the page from being usable.
    }
  }

  useEffect(() => { loadSeatAndPublishers(); loadBatches(); }, [seatId]);
  useEffect(() => { loadFloors(); }, [seatId, page, search]);

  function resetFileState() {
    setFile(null);
    setParsedRows(null);
    setParseError(null);
    setPreview(null);
    setForce(false);
    setChunkProgress(null);
  }

  function handleFile(e) {
    const f = e.target.files?.[0];
    setFile(f || null);
    setParsedRows(null);
    setParseError(null);
    setPreview(null);
    setForce(false);
    if (!f) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseCsv(String(reader.result));
        if (!rows.length) {
          setParseError('No data rows found in this file.');
          return;
        }
        setParsedRows(rows);
      } catch (err) {
        setParseError('Could not parse this file as CSV.');
      }
    };
    reader.onerror = () => setParseError('Could not read this file.');
    reader.readAsText(f);
  }

  function chunkRows(rows) {
    if (mode !== 'sourcesOnly' || rows.length <= SOURCES_ONLY_CHUNK_SIZE) return [rows];
    const chunks = [];
    for (let i = 0; i < rows.length; i += SOURCES_ONLY_CHUNK_SIZE) chunks.push(rows.slice(i, i + SOURCES_ONLY_CHUNK_SIZE));
    return chunks;
  }

  function aggregateSummaries(results) {
    const agg = {
      totalRows: 0, validRows: 0, invalidRows: 0, uniqueIdentities: 0,
      errors: [], errorsTruncated: false,
      sources: { inserted: 0, updated: 0, unchanged: 0 },
      floors: { inserted: 0, updated: 0, removed: 0, unchanged: 0 },
      needsForce: false,
    };
    for (const { summary } of results) {
      agg.totalRows += summary.totalRows;
      agg.validRows += summary.validRows;
      agg.invalidRows += summary.invalidRows;
      agg.uniqueIdentities += summary.uniqueIdentities;
      agg.sources.inserted += summary.sources.inserted;
      agg.sources.updated += summary.sources.updated;
      agg.sources.unchanged += summary.sources.unchanged;
      if (agg.errors.length < 50) agg.errors.push(...summary.errors.slice(0, 50 - agg.errors.length));
      if (summary.errorsTruncated || summary.invalidRows > summary.errors.length) agg.errorsTruncated = true;
    }
    return agg;
  }

  async function runPreview() {
    if (!publisherId || !parsedRows) return;
    setPreviewing(true);
    setPreview(null);
    setChunkProgress(null);
    try {
      const chunks = chunkRows(parsedRows);
      const results = [];
      for (let i = 0; i < chunks.length; i++) {
        if (chunks.length > 1) setChunkProgress({ current: i + 1, total: chunks.length });
        const body = mode === 'full'
          ? { publisherId: Number(publisherId), identityType, mode, currency: currency.trim() || 'USD', filename: file?.name, rows: chunks[i] }
          : { publisherId: Number(publisherId), identityType, mode, filename: file?.name, rows: chunks[i] };
        results.push(await api.runSeatBundleImport(seatId, body, { dryRun: true }));
      }
      setPreview(chunks.length > 1 ? { committed: false, summary: aggregateSummaries(results), chunked: true } : results[0]);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Preview failed.');
    } finally {
      setPreviewing(false);
      setChunkProgress(null);
    }
  }

  async function confirmImport() {
    if (!publisherId || !parsedRows) return;
    setImporting(true);
    setChunkProgress(null);
    const results = []; // declared outside try/catch so the catch block can report partial progress
    try {
      const chunks = chunkRows(parsedRows);
      for (let i = 0; i < chunks.length; i++) {
        if (chunks.length > 1) setChunkProgress({ current: i + 1, total: chunks.length });
        const body = mode === 'full'
          ? { publisherId: Number(publisherId), identityType, mode, currency: currency.trim() || 'USD', filename: file?.name, rows: chunks[i] }
          : { publisherId: Number(publisherId), identityType, mode, filename: file?.name, rows: chunks[i] };
        const result = await api.runSeatBundleImport(seatId, body, { dryRun: false, force });
        if (!result.committed) {
          // Guard tripped (full mode only -- e.g. the operator never actually checked "force").
          // Any earlier chunks in this run already committed for real (each chunk is its own
          // transaction) -- surface that honestly rather than pretending nothing happened.
          setPreview({ ...result, chunked: chunks.length > 1, partialCommits: results.length });
          toast.error(
            results.length > 0
              ? `Import needs confirmation -- ${results.length} of ${chunks.length} chunks already committed. Check "force" and re-run to continue.`
              : 'Import needs confirmation -- check "force this import" and try again.'
          );
          return;
        }
        results.push(result);
      }
      const agg = aggregateSummaries(results);
      toast.success(
        mode === 'full'
          ? `Imported: ${agg.floors.inserted} new floors, ${agg.floors.updated} updated, ${agg.floors.removed} removed; ${agg.sources.inserted} new sources.`
          : `Imported: ${agg.sources.inserted} new sources, ${agg.sources.updated} updated, ${agg.sources.unchanged} unchanged across ${agg.validRows.toLocaleString()} rows.`
      );
      resetFileState();
      setPage(0);
      loadFloors();
      loadBatches();
    } catch (err) {
      const base = err instanceof ApiError ? err.message : 'Import failed.';
      toast.error(results.length > 0 ? `${base} (${results.length} chunk(s) committed before the failure -- safe to re-run the same file, writes are idempotent.)` : base);
    } finally {
      setImporting(false);
      setChunkProgress(null);
    }
  }

  if (loading) return <div className="page"><LoadingState label="Loading seat" /></div>;
  if (error || !seat) {
    return (
      <div className="page">
        <EmptyState title="Seat not found" message={error || 'This seat may have been deleted.'} />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(floors.total / PAGE_SIZE));

  return (
    <div className="page">
      <PageHeader
        title={`Bundle floors -- ${seat.name}`}
        subtitle={<code className="code-chip">{seat.seatId}</code>}
        actions={
          <button className="btn btn--ghost" onClick={() => navigate(`/inbound-seats/${seatId}`)}>
            &larr; Seat detail
          </button>
        }
      />

      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Import a supply feed</h2>
        </div>

        <div className="field">
          <span className="field__label">Feed type</span>
          <div className="checkbox-row">
            <label className="field field--checkbox field--checkbox-inline">
              <input type="radio" name="mode" checked={mode === 'full'} onChange={() => { setMode('full'); resetFileState(); }} />
              <span>Full -- bundle/domain + media type + floor price (e.g. Cuedart)</span>
            </label>
            <label className="field field--checkbox field--checkbox-inline">
              <input type="radio" name="mode" checked={mode === 'sourcesOnly'} onChange={() => { setMode('sourcesOnly'); resetFileState(); }} />
              <span>Routing only -- a bare bundle/domain list, no pricing (e.g. Pumpkin Ads)</span>
            </label>
          </div>
        </div>

        <p className="card__hint">
          {mode === 'full' ? (
            <>
              Upload a CSV export of a bulk supply feed. Every row maps one bundle ID or domain +
              media type to a floor CPM; every unique bundle/domain is also routed to the publisher
              you choose below, so this seat's real inbound traffic for those bundles resolves to a
              BidDigi publisher -- see the "what this does" note below the form. Only CSV is
              accepted here -- export your spreadsheet to CSV first (File &rarr; Save As / Export
              &rarr; CSV in Excel, Numbers, or Google Sheets).
            </>
          ) : (
            <>
              Upload a CSV export of a bare bundle/domain list -- one column, no floor prices. Every
              unique bundle/domain is routed to the publisher you choose below; since there's no
              floor data, each one falls through to that publisher's own default floor (the normal
              fallback for any bundle with no explicit floor row). Large files are automatically
              split into several sequential import calls -- you'll see progress below. Sources are
              only ever added or updated by this mode, never removed.
            </>
          )}
        </p>

        <div className="form">
          <label className="field">
            <span className="field__label">Route matched bundles/domains to publisher</span>
            <select value={publisherId} onChange={(e) => { setPublisherId(e.target.value); setPreview(null); }}>
              <option value="">Select a publisher&hellip;</option>
              {publishers.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.domain})</option>
              ))}
            </select>
          </label>

          <div className="field">
            <span className="field__label">Identity type</span>
            <div className="checkbox-row">
              <label className="field field--checkbox field--checkbox-inline">
                <input type="radio" name="identityType" checked={identityType === 'bundle'} onChange={() => { setIdentityType('bundle'); setPreview(null); }} />
                <span>App bundle ID</span>
              </label>
              <label className="field field--checkbox field--checkbox-inline">
                <input type="radio" name="identityType" checked={identityType === 'domain'} onChange={() => { setIdentityType('domain'); setPreview(null); }} />
                <span>Site domain</span>
              </label>
            </div>
          </div>

          {mode === 'full' && (
            <label className="field">
              <span className="field__label">Floor currency</span>
              <input value={currency} onChange={(e) => { setCurrency(e.target.value.toUpperCase()); setPreview(null); }} style={{ width: '6rem' }} maxLength={3} />
            </label>
          )}

          <label className="field">
            <span className="field__label">CSV file</span>
            <input type="file" accept=".csv,text/csv" onChange={handleFile} />
          </label>

          {parseError && <div className="form-error">{parseError}</div>}
          {parsedRows && !parseError && (
            <div className="card__hint">
              Parsed {parsedRows.length.toLocaleString()} data rows from {file?.name}.
              {mode === 'sourcesOnly' && parsedRows.length > SOURCES_ONLY_CHUNK_SIZE && (
                <> Will be sent as {Math.ceil(parsedRows.length / SOURCES_ONLY_CHUNK_SIZE)} sequential batches of up to {SOURCES_ONLY_CHUNK_SIZE.toLocaleString()} rows each.</>
              )}
            </div>
          )}

          {chunkProgress && (
            <div className="card__hint">Processing batch {chunkProgress.current} of {chunkProgress.total}&hellip;</div>
          )}

          <div className="button-row">
            <button className="btn btn--primary" disabled={!publisherId || !parsedRows || previewing} onClick={runPreview}>
              {previewing ? 'Checking…' : 'Preview import'}
            </button>
          </div>
        </div>

        {preview && (
          <ImportPreview
            preview={preview}
            sourcesOnly={mode === 'sourcesOnly'}
            force={force}
            setForce={setForce}
            importing={importing}
            onConfirm={confirmImport}
          />
        )}
      </section>

      <section className="card card--flush">
        <div className="card__header card__header--padded">
          <h2 className="card__title">Current bundle floors ({floors.total.toLocaleString()})</h2>
          <div className="button-row">
            <input
              placeholder="Search bundle/domain…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); setSearch(searchInput.trim()); } }}
            />
            <button className="btn btn--ghost btn--sm" onClick={() => { setPage(0); setSearch(searchInput.trim()); }}>
              Search
            </button>
          </div>
        </div>
        {floorsLoading ? (
          <div className="card__padded"><LoadingState label="Loading floors" /></div>
        ) : floors.rows.length === 0 ? (
          <div className="card__padded">
            <EmptyState title="No bundle floors yet" message="Import a feed above to get started. Routing-only imports don't add rows here -- see Sources on the seat's detail page." />
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Bundle / Domain</th>
                    <th>Media type</th>
                    <th>Floor</th>
                    <th>Monthly opportunities</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {floors.rows.map((r, i) => (
                    <tr key={`${r.bundle_id || r.domain}-${r.media_type}-${i}`}>
                      <td><code className="code-chip">{r.bundle_id || r.domain}</code></td>
                      <td>{r.media_type}</td>
                      <td>{Number(r.floor_cpm).toFixed(4)} {r.floor_currency}</td>
                      <td>{r.monthly_opportunities ? Number(r.monthly_opportunities).toLocaleString() : '—'}</td>
                      <td>{new Date(r.updated_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="button-row card__padded">
                <button className="btn btn--ghost btn--sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>&larr; Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn--ghost btn--sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next &rarr;</button>
              </div>
            )}
          </>
        )}
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Import history</h2>
        </div>
        {batches.length === 0 ? (
          <p className="card__hint">No imports recorded yet for this seat.</p>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Mode</th>
                  <th>File</th>
                  <th>Publisher</th>
                  <th>Rows</th>
                  <th>Sources</th>
                  <th>Floors</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id}>
                    <td>{new Date(b.created_at).toLocaleString()}</td>
                    <td>{b.mode === 'sources_only' ? 'Routing only' : 'Full'}</td>
                    <td>{b.filename || '—'}{b.forced && <span className="cell-subtitle"> (forced)</span>}</td>
                    <td>{b.publisher_name}</td>
                    <td>{b.valid_rows}/{b.total_rows} valid</td>
                    <td>+{b.sources_inserted} new / {b.sources_updated} updated</td>
                    <td>{b.mode === 'sources_only' ? 'n/a' : `+${b.floors_inserted} / ~${b.floors_updated} / -${b.floors_removed}`}</td>
                    <td>{b.actor || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function ImportPreview({ preview, sourcesOnly, force, setForce, importing, onConfirm }) {
  const { summary } = preview;
  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <div className="card__header">
        <h3 className="card__title">Preview</h3>
      </div>
      <p>
        {summary.totalRows.toLocaleString()} rows parsed &mdash; {summary.validRows.toLocaleString()} valid,{' '}
        {summary.invalidRows.toLocaleString()} invalid, {summary.uniqueIdentities.toLocaleString()} unique bundles/domains.
        {preview.chunked && ' (Totals across all batches.)'}
      </p>
      <ul>
        <li>
          <strong>Sources (routing):</strong> +{summary.sources.inserted} new, {summary.sources.updated} updated,{' '}
          {summary.sources.unchanged} unchanged. Sources are never removed by an import.
        </li>
        {!sourcesOnly && (
          <li>
            <strong>Floors (pricing):</strong> +{summary.floors.inserted} new, {summary.floors.updated} updated,{' '}
            {summary.floors.removed} removed, {summary.floors.unchanged} unchanged.
          </li>
        )}
        {sourcesOnly && (
          <li>No floor data in this feed -- every bundle/domain above falls through to the publisher's own default floor.</li>
        )}
      </ul>

      {summary.invalidRows > 0 && (
        <details>
          <summary>{summary.invalidRows} invalid row{summary.invalidRows === 1 ? '' : 's'} (skipped, not imported){summary.errorsTruncated ? ' -- showing a sample' : ''}</summary>
          <ul>
            {summary.errors.map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
          </ul>
        </details>
      )}

      {!sourcesOnly && summary.needsForce && (
        <div className="form-error">
          This import would remove {summary.floors.removed} of this seat's existing floor rows --
          more than half. That usually means a truncated or wrong file. If this is really intended
          (e.g. the source genuinely dropped most of this inventory), check the box below and import
          again.
          {preview.partialCommits > 0 && (
            <><br /><strong>{preview.partialCommits} batch(es) from this run already committed for real</strong> before this guard tripped -- checking "force" and re-running will only re-process the remaining batches (writes are idempotent, so this is safe).</>
          )}
          <label className="field field--checkbox field--checkbox-inline" style={{ marginTop: '0.5rem' }}>
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            <span>Force this import anyway</span>
          </label>
        </div>
      )}

      <div className="button-row">
        <button
          className="btn btn--primary"
          disabled={importing || (!sourcesOnly && summary.needsForce && !force) || summary.validRows === 0}
          onClick={onConfirm}
        >
          {importing ? 'Importing…' : 'Confirm import'}
        </button>
      </div>
    </div>
  );
}
